import _ from 'lodash';
import promiseUtil from './promiseUtil';
import audioAnalyser, {setAudioContext} from './audioAnalyser';

const fonts = [
    {url: 'assets/font/SofiaPro.otf', weight: 'normal'},
    {url: 'assets/font/SofiaProSemiBold.ttf', weight: 'bold'},
    {url: 'assets/font/SofiaProBold.otf', weight: '900'},
];

export const audios = {
    round1: 'audio/round1.mp3',
    round2: 'audio/round2.mp3',
    round3: 'audio/round3.mp3',
    round4: 'audio/round4.mp3',
    round5: 'audio/round5.mp3',
    round6: 'audio/round6.mp3',
    round1Game: 'audio/round1Game.mp3',
    round2Game: 'audio/round2Game.mp3',
    round3Game: 'audio/round3Game.mp3',
    round4Game: 'audio/round4Game.mp3',
    round5Game: 'audio/round5Game.mp3',
    round6Game: 'audio/round6Game.mp3',
    intro: 'audio/sound_design.mp3',
    startGame: 'audio/sound_transition.mp3',
    brad: 'audio/sound_paper.mp3',
    right: 'audio/sound_peaceful.mp3',
    wrong: 'audio/sound_vibrant.mp3',
    analysis: 'audio/sound_transition.mp3',
    nextRound: 'audio/sound_transition.mp3',
    discussion: 'audio/sound_design.mp3',
    infoPanel: 'audio/sound_switch.mp3',
    countdown: 'audio/sound_countdown.mp3',
    alarm: 'audio/sound_alarm.mp3',
    team: 'audio/sound_team.mp3',
    done: 'audio/sound_soft.mp3',
};

let loadResolve;
export const ready = new Promise(resolve => (loadResolve = resolve));

window.AudioContext = window.AudioContext || window.webkitAudioContext;
const audioContext = new AudioContext();

const audioBuffers = {};
const playingAudios = new Map();

loadAll().catch(console.error);

async function loadAll() {
    await loadFonts();
    await loadAudios(audios);
    loadResolve();
}

async function loadFonts() {
    if (!('FontFace' in window)) {
        return;
    }

    for (const font of fonts) {
        const {url, weight} = font;
        const fontFace = new FontFace('SofiaPro', `url(${url})`);
        fontFace.weight = weight;
        await fontFace.load();
        document.fonts.add(fontFace);
    }
}

async function loadAudios(audios) {
    const audioResponseList = await loadMedias(audios, 'arraybuffer');

    for (const {source, response} of audioResponseList) {
        const audioBuffer = await decodeAudioData(response);
        audioBuffers[source] = audioBuffer;
    }
}

async function loadMedias(medias, responseType) {
    const loadPromises = _.uniq(_.values(medias)).map(source =>
        loadMedia(source, responseType)
    );
    return await Promise.all(loadPromises);
}

function loadMedia(source, responseType) {
    return new Promise(resolve => {
        const request = new XMLHttpRequest();
        request.open('GET', source, true);
        request.responseType = responseType;

        request.onload = function() {
            resolve({
                source,
                response: this.response,
            });
        };

        request.send();
    });
}

function decodeAudioData(arrayBuffer) {
    const callback = promiseUtil.makeCallback();

    audioContext.decodeAudioData(
        arrayBuffer,
        audioBuffer => callback(null, audioBuffer),
        callback
    );

    return callback.promise;
}

export function resume() {
    audioContext.resume();
    setAudioContext(audioContext);
    audioAnalyser.initialize();
}

export async function play(audioKey, {duration = -1, analyze = false} = {}) {
    const source = audios[audioKey];
    const audioBuffer = audioBuffers[source];
    const bufferSourceNode = audioContext.createBufferSource();
    bufferSourceNode.buffer = audioBuffer;

    playingAudios.set(audioKey, bufferSourceNode);

    try {
        if (analyze) {
            audioAnalyser.setAudioSourceNode(bufferSourceNode);
        } else {
            bufferSourceNode.connect(audioContext.destination);
        }
        bufferSourceNode.start();

        if (duration >= 0) {
            setTimeout(() => {
                bufferSourceNode.stop();
            }, duration);
        }

        await waitForEvents(bufferSourceNode, ['ended']);
    } finally {
        playingAudios.delete(audioKey);
        bufferSourceNode.disconnect();
    }
}

function waitForEvents(target, events) {
    return new Promise(resolve => {
        const listener = () => {
            resolve();

            for (const event of events) {
                target.removeEventListener(event, listener);
            }
        };

        for (const event of events) {
            target.addEventListener(event, listener);
        }
    });
}

export async function pause(audioKey) {
    if (!playingAudios.has(audioKey)) {
        return;
    }

    const bufferSourceNode = playingAudios.get(audioKey);
    bufferSourceNode.stop();
}
